import {
  createListenerMiddleware,
  isAnyOf,
  type TypedStartListening,
} from '@reduxjs/toolkit';

import { sendAnalyticsInfo } from 'features/analytics/helpers';
import type { AppDispatch, RootState } from 'features/app/store/store';
import {
  connectCall,
  selectIsConnected,
} from 'features/call/call-ui-state/store';
import {
  clearCaptions,
  resetCallCaptions,
  selectCaptionsEnabled,
  startCaptionsSessionThunk,
} from 'features/call/call-captions/store';
import { isAnyHearingConnectedSelector } from 'features/call/call-hearing/store';
import { VoiceSessionDaoService } from 'features/voice-session/services';
import {
  setVoiceSession,
  setVoiceSessionId,
  voiceSessionFinished,
  voiceSessionIdSelector,
  voiceSessionReset,
  voiceSessionTeamingLeaveReset,
} from 'features/voice-session/store';

// Captions lifecycle is split on purpose:
// - callCaptionsSlice extraReducers: sync cleanup (lines, startedSessionIds)
// - this middleware: async start/stop API calls and hearing-disconnect clears
// User toggles go through toggleCaptionsThunk (not setCaptionsEnabled listeners)
// to avoid racing duplicate startCaptionsSessionThunk dispatches.

const listenerMiddleware = createListenerMiddleware();

type AppStartListening = TypedStartListening<RootState, AppDispatch>;
type ListenerApi = Parameters<Parameters<AppStartListening>[0]['effect']>[1];

const startListening = listenerMiddleware.startListening as AppStartListening;

const captionsStartActions = isAnyOf(
  connectCall,
  setVoiceSessionId,
  setVoiceSession
);

const captionsStopActions = isAnyOf(
  voiceSessionFinished,
  voiceSessionReset,
  voiceSessionTeamingLeaveReset,
  resetCallCaptions
);

const voiceSessionEndSources: Record<string, string> = {
  [voiceSessionFinished.type]: 'voice-session-end',
  [voiceSessionReset.type]: 'voice-session-reset',
  [voiceSessionTeamingLeaveReset.type]: 'voice-session-teaming-leave',
};

// String action types — .fulfilled creator imports break when thunks are mocked.
const hearingDisconnectFulfilledTypes = [
  'callHearing/notifyParticipantDisconnected/fulfilled',
  'call-hearing/disconnectById/fulfilled',
  'call-hearing/disconnectedFromCall/fulfilled',
] as const;

const maybeStartCaptionsSession = async (api: ListenerApi, source: string) => {
  const state = api.getState();

  if (!selectCaptionsEnabled(state) || !selectIsConnected(state)) {
    return;
  }

  const voiceSessionId = voiceSessionIdSelector(state);

  if (!voiceSessionId) {
    return;
  }

  await api.dispatch(startCaptionsSessionThunk({ source }));
};

const voiceSessionIdChanged = (
  action: Parameters<Parameters<AppStartListening>[0]['effect']>[0],
  api: ListenerApi
) => {
  const previousId = api.getOriginalState().voiceSession.sessionId;
  const nextId = setVoiceSession.match(action)
    ? action.payload.sessionId
    : api.getState().voiceSession.sessionId;

  return Boolean(nextId && nextId !== previousId);
};

const stopCaptionsFromPrevState = async (
  api: ListenerApi,
  {
    source,
    requireCaptionsEnabled,
    sendAnalytics,
  }: {
    source: string;
    requireCaptionsEnabled: boolean;
    sendAnalytics: boolean;
  }
) => {
  // extraReducers run before this effect and clear startedSessionIds, so read
  // pre-reducer state. Do not use stopCaptionsSessionThunk here — it would see
  // an empty startedSessionIds and skip the API call.
  const prevState = api.getOriginalState();
  const voiceSessionId = voiceSessionIdSelector(prevState);
  const wasStarted =
    voiceSessionId &&
    prevState.callCaptions.startedSessionIds.includes(voiceSessionId);

  if (
    !voiceSessionId ||
    !wasStarted ||
    (requireCaptionsEnabled && !selectCaptionsEnabled(prevState))
  ) {
    return;
  }

  if (sendAnalytics) {
    api.dispatch(
      sendAnalyticsInfo({
        Method: 'stopCaptionsSessionThunk',
        Message: 'Calling stop-captions API',
        properties: { voiceSessionId, source },
      })
    );
  }

  try {
    await VoiceSessionDaoService.stopCaptions(voiceSessionId);
  } catch (error) {
    console.warn(
      sendAnalytics
        ? 'Voice captions failed to stop'
        : 'Voice captions failed to stop on call reset',
      {
        error,
        voiceSessionId,
      }
    );
  }
};

startListening({
  matcher: captionsStartActions,
  effect: async (action, api) => {
    if (
      (setVoiceSessionId.match(action) || setVoiceSession.match(action)) &&
      !voiceSessionIdChanged(action, api)
    ) {
      return;
    }

    const source = connectCall.match(action)
      ? 'call-connected'
      : setVoiceSession.match(action)
        ? 'voice-session-ready'
        : 'voice-session-reconnect';

    await maybeStartCaptionsSession(api, source);
  },
});

startListening({
  matcher: captionsStopActions,
  effect: async (action, api) => {
    const isCallReset = resetCallCaptions.match(action);

    await stopCaptionsFromPrevState(api, {
      source: isCallReset
        ? 'call-reset'
        : (voiceSessionEndSources[action.type] ?? 'voice-session-end'),
      requireCaptionsEnabled: !isCallReset,
      sendAnalytics: !isCallReset,
    });
  },
});

startListening({
  predicate: (action) =>
    hearingDisconnectFulfilledTypes.includes(
      action.type as (typeof hearingDisconnectFulfilledTypes)[number]
    ),
  effect: async (_action, api) => {
    if (!isAnyHearingConnectedSelector(api.getState())) {
      api.dispatch(clearCaptions());
    }
  },
});

export const callCaptionsListenerMiddleware = listenerMiddleware.middleware;
